<template>
  <main class="faq-page bg-[#f5f7f4] text-slate-900">
    <section class="faq-hero relative isolate overflow-hidden bg-[#063c3f] text-white">
      <div class="faq-grid absolute inset-0 opacity-25"></div>
      <div class="absolute -right-24 -top-24 h-80 w-80 rounded-full bg-amber-300/20 blur-3xl"></div>
      <div class="absolute bottom-0 right-[8%] hidden h-56 w-56 translate-y-20 rounded-full border-[38px] border-white/5 lg:block"></div>
      <div class="relative mx-auto max-w-screen-xl px-4 pb-14 pt-7 sm:px-6 md:pb-20 lg:px-10">
        <JourneyPageLinks current="faq" variant="breadcrumb" dark />

        <div class="mt-12 grid items-end gap-10 lg:grid-cols-[1fr_420px]">
          <div class="max-w-3xl">
            <span class="inline-flex items-center gap-2 rounded-full border border-amber-300/30 bg-amber-300/10 px-3 py-1.5 text-[11px] font-extrabold uppercase tracking-[.16em] text-amber-200">
              <font-awesome-icon icon="fa-solid fa-question-circle" class="h-3 w-3" /> {{ content.eyebrow }}
            </span>
            <h1 class="mt-5 text-4xl font-black tracking-tight text-white md:text-6xl">{{ content.title }}</h1>
            <p class="mt-5 max-w-2xl text-sm leading-7 text-white/70 md:text-lg md:leading-8">{{ content.intro }}</p>
          </div>

          <div class="rounded-3xl border border-white/15 bg-white/10 p-3 shadow-2xl backdrop-blur-md">
            <label for="faq-search" class="sr-only">{{ content.searchPlaceholder }}</label>
            <div class="flex items-center gap-3 rounded-2xl bg-white px-4 py-3.5 text-slate-900 shadow-lg">
              <font-awesome-icon icon="fa-solid fa-search" class="text-teal-700 h-5 w-5" />
              <input id="faq-search" v-model.trim="search" type="search" :placeholder="content.searchPlaceholder" class="min-w-0 flex-1 border-0 bg-transparent p-0 text-sm outline-none ring-0 placeholder:text-slate-400 focus:ring-0" />
              <button v-if="search" type="button" class="flex h-7 w-7 items-center justify-center rounded-full bg-slate-100 text-slate-500 hover:bg-slate-200" :aria-label="content.clearSearch" @click="search = ''">
                <font-awesome-icon icon="fa-solid fa-times" class="h-3 w-3" />
              </button>
            </div>
            <div class="flex items-center justify-between px-2 pb-1 pt-3 text-xs text-white/60">
              <span>{{ visibleQuestionCount }} {{ content.results }}</span>
              <span>{{ content.categories.length }} {{ content.categoriesLabel.toLocaleLowerCase(localeCode) }}</span>
            </div>
          </div>
        </div>
      </div>
    </section>
   
    <section class="faq-content w-full px-4 sm:px-6 lg:px-10">
      <div class="mx-auto max-w-screen-xl">
        <div class="-mt-7 grid grid-cols-2 gap-2 md:grid-cols-4 lg:grid-cols-7">
        <button type="button" class="faq-category-card" :class="{ 'is-active': activeCategory === 'all' }" @click="setCategory('all')">
          <span class="faq-category-icon"><font-awesome-icon icon="fa-solid fa-list" class="h-4 w-4" /></span>
          <span>{{ content.allCategories }}</span>
          <small>{{ totalQuestions }}</small>
        </button>
        <button v-for="category in content.categories" :key="category.id" type="button" class="faq-category-card" :class="{ 'is-active': activeCategory === category.id }" @click="setCategory(category.id)">
          <span class="faq-category-icon"><font-awesome-icon :icon="category.icon" class="h-4 w-4" /></span>
          <span>{{ category.shortTitle }}</span>
          <small>{{ category.items.length }}</small>
        </button>
      </div>

      <div class="mt-10 grid gap-8 lg:grid-cols-[minmax(0,1fr)_300px]">
        <div>
          <div v-if="filteredGroups.length" class="space-y-9">
            <section v-for="group in filteredGroups" :id="`faq-${group.id}`" :key="group.id" class="faq-group scroll-mt-28">
              <div class="mb-4 flex items-start gap-3">
                <span class="flex h-11 w-11 flex-none items-center justify-center rounded-2xl bg-teal-700 text-white shadow-lg shadow-teal-900/10"><font-awesome-icon :icon="group.icon" /></span>
                <div>
                  <h2 class="text-xl font-black text-slate-900 md:text-2xl">{{ group.title }}</h2>
                  <p class="mt-1 text-sm leading-6 text-slate-500">{{ group.description }}</p>
                </div>
              </div>

              <div class="space-y-2.5">
                <details v-for="item in group.items" :key="item.question" class="faq-item group overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-[0_12px_35px_-30px_rgba(15,23,42,.65)]">
                  <summary class="flex cursor-pointer list-none items-center justify-between gap-5 px-4 py-4 text-left text-sm font-extrabold leading-6 text-slate-800 transition hover:text-teal-700 md:px-5 md:text-base">
                    <span>{{ item.question }}</span>
                    <span class="faq-chevron flex h-8 w-8 flex-none items-center justify-center rounded-full bg-teal-50 text-teal-700 transition"><font-awesome-icon icon="fa-solid fa-chevron-down" class="h-3 w-3" /></span>
                  </summary>
                  <div class="border-t border-slate-100 px-4 pb-5 pt-4 md:px-5">
                    <p class="text-sm leading-7 text-slate-600 md:text-[15px]">{{ item.answer }}</p>
                  </div>
                </details>
              </div>
            </section>
          </div>

          <div v-else class="rounded-3xl border border-dashed border-slate-300 bg-white px-6 py-14 text-center">
            <span class="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-amber-100 text-xl text-amber-700">
              <font-awesome-icon icon="fa-solid fa-search" class="h-4 w-4"/></span>
            <h2 class="mt-4 text-xl font-black">{{ content.noResultsTitle }}</h2>
            <p class="mx-auto mt-2 max-w-lg text-sm leading-6 text-slate-500">{{ content.noResultsText }}</p>
            <button type="button" class="mt-5 rounded-xl bg-teal-700 px-4 py-2.5 text-sm font-bold text-white hover:bg-teal-800" @click="resetFilters">{{ content.clearSearch }}</button>
          </div>
        </div>

        <aside>
          <div class="sticky top-24 space-y-4">
            <div class="overflow-hidden rounded-3xl bg-[#073c3f] p-6 text-white shadow-xl">
              <span class="flex h-11 w-11 items-center justify-center rounded-2xl bg-amber-300 text-[#073c3f]">
                <font-awesome-icon icon="fa-solid fa-headset"   class="h-4 w-4" /></span>
              <p class="mt-5 text-xs font-extrabold uppercase tracking-[.16em] text-amber-300">{{ content.contactEyebrow }}</p>
              <h2 class="mt-2 text-xl font-black leading-snug text-white">{{ content.contactTitle }}</h2>
              <p class="mt-3 text-sm leading-6 text-white/65">{{ content.contactText }}</p>
              <div class="mt-5 grid gap-2">
                <a href="tel:+908502414141" class="inline-flex items-center justify-center gap-2 rounded-xl bg-amber-400 px-4 py-3 text-sm font-extrabold text-[#123d3c] transition hover:bg-amber-300"><font-awesome-icon icon="fa-solid fa-phone" /> {{ content.call }}</a>
                <a :href="buildWhatsAppUrl()" target="_blank" rel="nofollow noopener noreferrer" class="inline-flex items-center justify-center gap-2 rounded-xl border border-white/15 bg-white/10 px-4 py-3 text-sm font-bold text-white transition hover:bg-white/20"><font-awesome-icon icon="fa-brands fa-whatsapp" /> {{ content.whatsapp }}</a>
              </div>
            </div>
            <a :href="getPath('/page/umre-rehberi')" class="group flex items-center gap-4 rounded-3xl border border-amber-200 bg-amber-50 p-5" @click.prevent="goPath('/page/umre-rehberi')">
              <span class="flex h-10 w-10 flex-none items-center justify-center rounded-xl bg-amber-400 text-[#173f3d]"><font-awesome-icon icon="fa-solid fa-book-open" /></span>
              <span class="text-sm font-extrabold leading-5 text-slate-800 group-hover:text-teal-700">{{ content.guideLink }}</span>
              <font-awesome-icon icon="fa-solid fa-arrow-right" class="ml-auto h-3 w-3 text-amber-700" />
            </a>
          </div>
        </aside>
      </div>
      </div>
    </section>
    <JourneyPageLinks current="faq" />
  </main>
</template>

<script setup>
import { faqContent } from '~/data/faq'
import { useCommon } from '~/composables/useCommon'
import JourneyPageLinks from '~/components/page/JourneyPageLinks.vue'
import { buildWhatsAppUrl } from '~/utils/whatsapp'

const { locale } = useI18n()
const { goPath, getPath } = useCommon()
const search = ref('')
const activeCategory = ref('all')

const language = computed(() => locale.value === 'en' ? 'en' : 'tr')
const localeCode = computed(() => language.value === 'en' ? 'en-US' : 'tr-TR')
const content = computed(() => faqContent[language.value])
const totalQuestions = computed(() => content.value.categories.reduce((total, category) => total + category.items.length, 0))

const normalizeText = (value) => String(value || '')
  .toLocaleLowerCase(localeCode.value)
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '')

const filteredGroups = computed(() => {
  const query = normalizeText(search.value)
  return content.value.categories
    .filter(category => activeCategory.value === 'all' || category.id === activeCategory.value)
    .map(category => ({
      ...category,
      items: query
        ? category.items.filter(item => normalizeText(`${item.question} ${item.answer}`).includes(query))
        : category.items
    }))
    .filter(category => category.items.length)
})
const visibleQuestionCount = computed(() => filteredGroups.value.reduce((total, category) => total + category.items.length, 0))

const setCategory = (category) => {
  activeCategory.value = category
  if (import.meta.client) window.requestAnimationFrame(() => document.querySelector('.faq-content')?.scrollIntoView({ behavior: 'smooth', block: 'start' }))
}

const resetFilters = () => {
  search.value = ''
  activeCategory.value = 'all'
}

const stripForSchema = (value) => String(value || '').replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim()

useHead(() => ({
  title: language.value === 'en' ? 'Frequently Asked Questions | İnanç Tour' : 'Sıkça Sorulan Sorular -  İnanç Tur',
  meta: [{
    name: 'description',
    content: content.value.intro
  }],
  script: [{
    type: 'application/ld+json',
    children: JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: content.value.categories.flatMap(category => category.items).map(item => ({
        '@type': 'Question',
        name: stripForSchema(item.question),
        acceptedAnswer: { '@type': 'Answer', text: stripForSchema(item.answer) }
      }))
    })
  }]
}))
</script>

<style scoped>
.faq-page section { margin: 0; }
.faq-page .faq-hero { padding: 0; }
.faq-page .faq-content { padding-top: 0; padding-bottom: 4.5rem; }
.faq-page .faq-group { padding: 0; }
.faq-page p { margin-bottom: 0; }
.faq-page h1 { margin: 0; line-height: 1.06; }
.faq-page h2 { margin: 0; line-height: 1.25; }
.faq-page h3 { margin: 0; line-height: 1.3; }

.faq-grid {
  background-image: radial-gradient(circle at 1px 1px, rgba(255,255,255,.22) 1px, transparent 0);
  background-size: 27px 27px;
  mask-image: linear-gradient(to right, black, transparent 82%);
}

.faq-category-card {
  position: relative;
  display: flex;
  min-height: 118px;
  flex-direction: column;
  align-items: flex-start;
  justify-content: space-between;
  gap: .2rem;
  border: 1px solid #e2e8f0;
  border-radius: 1rem;
  background: #fff;
  padding: .9rem;
  text-align: left;
  color: #334155;
  box-shadow: 0 16px 38px -32px rgba(15,23,42,.7);
  transition: transform .2s ease, border-color .2s ease, color .2s ease;
}

.faq-category-card:hover { transform: translateY(-2px); border-color: #5eead4; color: #0f766e; }
.faq-category-card.is-active { border-color: #0f766e; background: #0f766e; color: #fff; box-shadow: 0 16px 35px -22px rgba(15,118,110,.9); }
.faq-category-card > span:nth-child(2) { font-size: .75rem; font-weight: 800; line-height: 1.25; }
.faq-category-card small { position: absolute; right: .7rem; top: .7rem; font-size: .65rem; font-weight: 900; opacity: .55; }
.faq-category-icon { display: flex; height: 2rem; width: 2rem; align-items: center; justify-content: center; border-radius: .65rem; background: #f0fdfa; color: #0f766e; }
.faq-category-card.is-active .faq-category-icon { background: rgba(255,255,255,.14); color: #fde68a; }

.faq-item[open] { border-color: #99f6e4; box-shadow: 0 16px 40px -30px rgba(13,148,136,.75); }
.faq-item summary::-webkit-details-marker { display: none; }
.faq-item[open] .faq-chevron { transform: rotate(180deg); background: #0f766e; color: #fff; }

@media (min-width: 768px) {
  .faq-category-card { min-height: 52px; padding: 1rem; border-radius: 1.25rem; }
  .faq-category-card > span:nth-child(2) { font-size: .8rem; }
}
</style>
